/**
 * "Couldn't check" — V109-1-13 / LM-9, reworked by the V109-1-19 review (rounds 1–3). One result GAC
 * Pro could not judge: never counted as pass or fail by any surface that renders it.
 *
 *   CouldntCheckChip   the status-family pill with the "unknown" glyph (beside Pass / Fail), on every
 *                      surface — readiness, control detail, Today and Setup (R2-H6); `count` for a tally
 *   CouldntCheckCount  the hero count ("4 criteria couldn't be checked"), one component for SOC 2,
 *                      FedRAMP and GovRAMP, the glyph inline (R3 Medium)
 *   CouldntCheckWhy    the reason words, each reason once, with how much it leaves unverified and the
 *                      resource names; the Evidence column of a row
 *   CouldntCheckFixes  the fix per source, named for it ("Reconnect Google Cloud"), linking to where the
 *                      fix happens (B1, R3); compact adds a short muted "who" suffix; a fix GAC Pro makes
 *                      itself says when it collects next ("shortly" once due) with a quiet "Collect again";
 *                      a fix GAC Pro owns (R2-B2) says so and offers "Attach evidence instead"
 *   ShowAllReasons     ONE row-level toggle for the reasons and the fixes (R3 Low)
 *   CouldntCheckNote   the full note (words, resources, who can fix it, the fix, Details) for pages
 *                      with room
 *   AcceptRisk         "Accept the risk while this is fixed" → the minimal acceptance form, once per
 *                      list (owner / admin; R3 Medium); AcceptedLine shows one that holds (R2-H5)
 *
 * Raw provider text stays behind "Details". Never a dead end: every note carries its action.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, StatusGlyph } from '@aigovops/design-system';
import { couldntCheckEntry, isCouldntCheckReason, sourceNameOf, type CouldntCheckReason } from '@aigovops/core';
import type { RiskAcceptanceSummary } from '../lib/api';
import { dayText, distinctFixes, fixHref as fixHrefOf, nextCollectionText, type CouldntItem, type FixContext, type FixLink } from '../lib/couldnt-check';
import { AcceptRiskForm, type AcceptRiskTarget } from './AcceptRiskForm';
import './couldnt-check.css';

export const COULDNT_CHECK_LABEL = "Couldn't check";

/** Setup, opened on the step for the fix and (when known) its subject — the B1 link. */
export const fixHref = fixHrefOf;

/** The chip shown beside Pass / Fail: a status pill with the "unknown" glyph, never amber, never an outline box. */
export function CouldntCheckChip({ count }: { count?: number } = {}): JSX.Element {
  return <Badge className="v75-status cc-status"><StatusGlyph kind="unknown" /> {count != null ? `${count} ${COULDNT_CHECK_LABEL}` : COULDNT_CHECK_LABEL}</Badge>;
}

/** R3 (Medium): the hero count, one component everywhere, the glyph inline with the words. */
export function CouldntCheckCount({ count, noun, onClick }: { count: number; noun: [string, string]; onClick: () => void }): JSX.Element | null {
  if (count <= 0) return null;
  return (
    <p className="cc-count-line" data-part="couldnt-check-count">
      <button type="button" className="cc-count" onClick={onClick}>
        <StatusGlyph kind="unknown" /><span>{`${count} ${count === 1 ? noun[0] : noun[1]} couldn't be checked`}</span>
      </button>
    </p>
  );
}

const SHORT_FIXER: Record<string, string> = {
  source_admin: 'needs an admin of the service', engineering: 'needs your engineering team', compliance_lead: 'needs your compliance lead',
};

interface ReasonRow { reason: CouldntCheckReason; count: number | null; resources: string[] }

/** One line per reason: sources with the same reason add up. */
function byReason(items: readonly CouldntItem[]): ReasonRow[] {
  const m = new Map<CouldntCheckReason, ReasonRow>();
  for (const i of items) {
    const cur = m.get(i.reason);
    if (!cur) m.set(i.reason, { reason: i.reason, count: i.count, resources: [...i.resources] });
    else { cur.count = cur.count != null && i.count != null ? cur.count + i.count : cur.count ?? i.count; cur.resources.push(...i.resources.filter((r) => !cur.resources.includes(r))); }
  }
  return [...m.values()];
}

/** How many reasons and fixes a row has beyond `max` (the row-level toggle shows when > 0). */
export function hiddenCount(items: readonly CouldntItem[], prefix: string, ctx: FixContext = {}, max = 2): number {
  return Math.max(0, byReason(items).length - max) + Math.max(0, distinctFixes(prefix, items, ctx).length - max);
}

/** R3 (Low): ONE toggle per row for the reasons and their fixes. */
export function ShowAllReasons({ items, expanded, onToggle, max = 2 }: { items: readonly CouldntItem[]; expanded: boolean; onToggle: () => void; max?: number }): JSX.Element | null {
  const total = byReason(items).length;
  if (total <= max && items.length <= max) return null;
  return <button type="button" className="cc-more" aria-expanded={expanded} data-part="show-all-reasons" onClick={onToggle}>{expanded ? 'Show fewer' : `Show all ${total} reasons`}</button>;
}

/** Every reason once (the first `max` until the row is expanded), each with what it leaves unverified. */
export function CouldntCheckWhy({ items, unit = 'control', max = 2, expanded = false }: { items: readonly CouldntItem[]; unit?: 'control' | 'resource'; max?: number; expanded?: boolean }): JSX.Element {
  const rows = byReason(items);
  const shown = expanded ? rows : rows.slice(0, max);
  return (
    <ul className="cc-why" data-part="couldnt-check-why">
      {shown.map((v) => (
        <li key={v.reason} data-reason={v.reason}>
          <span className="cc-words">{couldntCheckEntry(v.reason).words}</span>
          {v.count != null ? <span className="cc-subject">{` · ${v.count} ${unit}${v.count === 1 ? '' : 's'}`}</span> : null}
          {v.resources.length ? <span className="cc-subject">{`: ${v.resources.slice(0, 3).join(', ')}${v.resources.length > 3 ? ` and ${v.resources.length - 3} more` : ''}`}</span> : null}
        </li>
      ))}
    </ul>
  );
}

/** The next collection of the sources behind one fix (the soonest). */
function nextOf(items: readonly CouldntItem[], prefix: string, ctx: FixContext, f: FixLink): string | null {
  const times = items.filter((i) => distinctFixes(prefix, [i], ctx)[0]?.href === f.href).map((i) => i.next_collection_at).filter((x): x is string => !!x).sort();
  return times[0] ?? null;
}
const sourceOf = (items: readonly CouldntItem[], prefix: string, ctx: FixContext, f: FixLink): string | null =>
  sourceNameOf(items.find((i) => distinctFixes(prefix, [i], ctx)[0]?.href === f.href)?.kind ?? null);

/** One fix, as a link to where it happens. */
function FixLine({ f, items, prefix, ctx, compact }: { f: FixLink; items: readonly CouldntItem[]; prefix: string; ctx: FixContext; compact: boolean }): JSX.Element {
  if (!f.view.customer_step) {
    // R2-B2: GAC Pro owns the fix (it approves the settings to compare against); the customer's way forward is evidence.
    return <span className="cc-fix-line" data-part="couldnt-check-owned"><span className="cc-auto">GAC Pro is approving these · </span>
      <Link className="cc-fix" to={f.href} data-part="couldnt-check-fix">{f.label}</Link></span>;
  }
  if (compact && f.view.fixer === 'gac_pro' && f.view.enabler_code === 'fix:collect-again') {
    const source = sourceOf(items, prefix, ctx, f);
    return <span className="cc-fix-line cc-auto" data-part="couldnt-check-auto">
      {`GAC Pro collects again ${nextCollectionText(nextOf(items, prefix, ctx, f))}`}
      {/* No dead end (CLAUDE.md): a quiet way to retry now. Label-in-name: the accessible name starts with the visible words. */}
      {' · '}<Link className="cc-fix" to={f.href} data-part="couldnt-check-fix" aria-label={source ? `Collect again from ${source}` : undefined}>Collect again</Link>
    </span>;
  }
  return <span className="cc-fix-line"><Link className="cc-fix" to={f.href} data-part="couldnt-check-fix">{f.label}</Link>
    {compact && SHORT_FIXER[f.view.fixer] ? <span className="cc-subject">{` · ${SHORT_FIXER[f.view.fixer]}`}</span> : null}</span>;
}

/** The fix per source. `compact`: a short muted "who" suffix; GAC Pro's own retries say when, not a call to action. */
export function CouldntCheckFixes({ items, prefix, ctx = {}, compact = false, max = 2, expanded = false }: {
  items: readonly CouldntItem[]; prefix: string; ctx?: FixContext; compact?: boolean; max?: number; expanded?: boolean;
}): JSX.Element {
  const fixes = distinctFixes(prefix, items, ctx);
  const shown = expanded ? fixes : fixes.slice(0, max);
  return (
    <div className={`cc-fixes${compact ? ' cc-fixes--compact' : ''}`} data-part="couldnt-check-fixes">
      {shown.map((f) => <FixLine key={f.href} f={f} items={items} prefix={prefix} ctx={ctx} compact={compact} />)}
    </div>
  );
}

/** R3 (R2-H5): an acceptance that holds: shown and remembered, never a clear. */
export function AcceptedLine({ acceptance, ownerName }: { acceptance: RiskAcceptanceSummary; ownerName: string | null }): JSX.Element {
  return <p className="cc-subject cc-accepted" data-part="risk-accepted">{`Risk accepted until ${dayText(acceptance.until)}${ownerName ? ` by ${ownerName}` : ''} · still couldn't check`}</p>;
}

export interface AcceptRiskProps {
  slug: string; viewerId: string;
  /** The items of the list without an acceptance; one form for the list (R3 Medium). */
  targets: readonly AcceptRiskTarget[];
  onDone?: (message: string, target: AcceptRiskTarget) => void;
}

/**
 * "Accept the risk while this is fixed" → the minimal acceptance form, inline, once per list or page.
 * R2-H8: Cancel / Escape return focus to the trigger; success focuses the confirmation (its status
 * region is mounted empty, so the words are announced when they arrive).
 */
export function AcceptRisk({ slug, viewerId, targets, onDone }: AcceptRiskProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState('');
  // Where focus goes once the form has closed and the page has re-rendered (an effect, never before the commit).
  const [focusTo, setFocusTo] = useState<'trigger' | 'done' | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const confirmation = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (!focusTo || open) return;
    (focusTo === 'trigger' ? trigger.current : confirmation.current)?.focus();
    setFocusTo(null);
  }, [focusTo, open]);
  return (
    <div className="cc-accept-wrap">
      <p className="cc-subject" role="status" aria-live="polite" tabIndex={-1} ref={confirmation} data-part="accept-risk-done">{done}</p>
      {open || !targets.length ? null : <button ref={trigger} type="button" className="cc-more" data-part="accept-risk" onClick={() => { setDone(''); setOpen(true); }}>Accept the risk while this is fixed</button>}
      {open ? <AcceptRiskForm slug={slug} viewerId={viewerId} targets={targets} onCancel={() => { setOpen(false); setFocusTo('trigger'); }}
        onDone={(m, t) => { setOpen(false); setDone(m); setFocusTo('done'); onDone?.(m, t); }} /> : null}
    </div>
  );
}

/**
 * The full note: each reason with its resources, who can fix it, the fix (named for the source) and
 * raw provider text behind Details. `reason` alone keeps the V109-1-13 call shape.
 */
export function CouldntCheckNote({ reason, items, prefix, ctx = {}, kind = null, compact = false, detail = null, subject = null }: {
  reason?: string; items?: readonly CouldntItem[]; prefix: string; ctx?: FixContext; kind?: string | null; compact?: boolean; detail?: string | null;
  /** What could not be checked ("1 of 2 resources"), when the caller knows. */
  subject?: string | null;
}): JSX.Element {
  const list: CouldntItem[] = items?.length ? [...items] : [{
    reason: (isCouldntCheckReason(reason) ? reason : 'collector_error'), connector_id: null, kind, system_id: null, count: null, resources: [], next_collection_at: null,
  }];
  const first = list[0]!;
  const fixes = distinctFixes(prefix, list, ctx);
  return (
    <div className={`cc-note${compact ? ' cc-note--compact' : ''}`} data-part="couldnt-check" data-reason={first.reason}>
      {list.length === 1 && !first.resources.length
        ? <p className="cc-words">{couldntCheckEntry(first.reason).words}{subject ? <span className="cc-subject">{` · ${subject}`}</span> : null}</p>
        : <><CouldntCheckWhy items={list} unit="resource" expanded />{subject ? <p className="cc-subject">{subject}</p> : null}</>}
      {compact ? null : <p className="cc-fixer">{[...new Set(fixes.map((f) => f.view.fixer_words))].join(' · ')}</p>}
      {fixes.map((f) => <Link key={f.href} className="cc-fix" to={f.href} data-part="couldnt-check-fix">{f.label}</Link>)}
      {detail ? <details className="cc-details"><summary>Details</summary><p>{detail}</p></details> : null}
    </div>
  );
}
